import { useState } from 'react';
import type { Tunable } from '@studio/shared';

type Props = {
  tunables: Tunable[];
  busy: boolean;
  /** The line currently being dragged, so the code panel can highlight it. */
  onFocusLine: (line: number | null) => void;
  onApply: (changes: { name: string; value: number }[]) => void;
};

/**
 * Sliders over the numbers in the child's own game.
 *
 * The label under each slider is the model's own trailing comment — "how high
 * you jump", "how fast the spiders scuttle" — so the child is reading real code
 * without being told they are. Dragging a slider highlights the exact line it
 * controls in the code panel beside it, which is the whole point: the moment
 * "the AI made it" becomes "that line makes it do that".
 *
 * Nothing here costs credits. Turning a dial is something you did, not something
 * you asked for.
 */
export function TweakPanel({ tunables, busy, onFocusLine, onApply }: Props) {
  const [values, setValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(tunables.map((t) => [t.name, t.value])),
  );

  const changed = tunables
    .filter((t) => values[t.name] !== t.value)
    .map((t) => ({ name: t.name, value: values[t.name]! }));

  if (tunables.length === 0) return null;

  return (
    <div className="tweak">
      <h3>Change how it plays</h3>
      <p className="tweak__hint">
        Drag a slider and watch the code light up — that line is what makes it happen.
      </p>

      {tunables.map((tunable) => (
        <label
          key={tunable.name}
          className="tweak__row"
          onPointerEnter={() => onFocusLine(tunable.line)}
          onPointerLeave={() => onFocusLine(null)}
        >
          <span className="tweak__label">
            {tunable.label}
            <em>{values[tunable.name]}</em>
          </span>
          <input
            type="range"
            min={tunable.min}
            max={tunable.max}
            step={tunable.step}
            value={values[tunable.name]}
            onFocus={() => onFocusLine(tunable.line)}
            onBlur={() => onFocusLine(null)}
            onChange={(e) => {
              onFocusLine(tunable.line);
              setValues({ ...values, [tunable.name]: Number(e.target.value) });
            }}
          />
          <code className="tweak__code">
            {tunable.name} = {values[tunable.name]}
          </code>
        </label>
      ))}

      <div className="tweak__actions">
        <button
          className="go"
          disabled={busy || changed.length === 0}
          onClick={() => onApply(changed)}
        >
          {busy ? 'Changing…' : `Try it${changed.length ? ` (${changed.length})` : ''}`}
        </button>
        {changed.length > 0 && (
          <button
            className="quiet"
            onClick={() =>
              setValues(Object.fromEntries(tunables.map((t) => [t.name, t.value])))
            }
          >
            Put it back
          </button>
        )}
        <span className="tweak__free">Free — you're doing this one yourself</span>
      </div>
    </div>
  );
}
